import { useInfiniteQuery, useQuery, type QueryClient } from "@tanstack/react-query";
import type { Asset, EventFrame } from "@agentsaas/protocol";
import { useServices } from "../app/services";
import { isEvent } from "./realtime";

/**
 * P4 资产数据：资产库（按筛选条件游标分页）、详情（含版本）、下载地址（预签名，缓存到过期前）、生成任务。
 * project:<pid> 的 asset.ready → 让资产列表、相关详情和生成任务失效重拉。
 */

export type AssetKind = Asset["kind"];
export interface AssetFilter {
  kind?: AssetKind;
  tag?: string;
  q?: string;
}

export const ak = {
  all: (pid: string) => ["assets", pid] as const,
  list: (pid: string, f: AssetFilter) => ["assets", pid, "list", f.kind ?? "", f.tag ?? "", f.q ?? ""] as const,
  asset: (asid: string) => ["asset", asid] as const,
  download: (asid: string, version: number, variant: string) => ["asset", asid, "download", version, variant] as const,
  jobs: (pid: string) => ["mediaJobs", pid] as const,
};

const PAGE = 30;

export function useAssets(pid: string | undefined, f: AssetFilter, opts: { enabled?: boolean } = {}) {
  const { api } = useServices();
  const q = useInfiniteQuery({
    queryKey: ak.list(pid ?? "", f),
    enabled: !!pid && (opts.enabled ?? true),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.call("assetList", {
        params: { pid: pid! },
        query: { limit: PAGE, ...(pageParam ? { cursor: pageParam } : {}), ...(f.kind ? { kind: f.kind } : {}), ...(f.tag ? { tag: f.tag } : {}), ...(f.q ? { q: f.q } : {}) },
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  return {
    items: q.data?.pages.flatMap((p) => p.items) ?? [],
    loading: q.isPending && (opts.enabled ?? true),
    hasMore: !!q.hasNextPage,
    loadMore: () => void q.fetchNextPage(),
    loadingMore: q.isFetchingNextPage,
    error: q.error,
  };
}

export function useAsset(asid: string | undefined) {
  const { api } = useServices();
  return useQuery({ queryKey: ak.asset(asid ?? ""), enabled: !!asid, queryFn: () => api.call("assetGet", { params: { asid: asid! } }) });
}

/**
 * 预签名下载地址；在过期前 1 分钟视为过时。
 * optional：变体可能不存在（例如列表里不知道模型有没有缩略图，服务端会返回 400），失败不重试。
 */
export function useAssetUrl(asid: string | undefined, version: number | undefined, variant: "original" | "thumb" | "preview" = "original", opts: { optional?: boolean } = {}) {
  const { api } = useServices();
  return useQuery({
    queryKey: ak.download(asid ?? "", version ?? 0, variant),
    enabled: !!asid && !!version,
    staleTime: 10 * 60_000,
    ...(opts.optional ? { retry: false } : {}),
    queryFn: async () => {
      const r = await api.call("assetDownload", { params: { asid: asid! }, query: { version, variant } });
      return r;
    },
  });
}

export function useMediaJobs(pid: string | undefined) {
  const { api } = useServices();
  return useQuery({
    queryKey: ak.jobs(pid ?? ""),
    enabled: !!pid,
    queryFn: async () => (await api.call("mediaJobList", { params: { pid: pid! }, query: { limit: 50 } })).items,
    // queued → running 没有推送事件；有进行中的任务时低频轮询，完成由 asset.ready 立即刷新
    refetchInterval: (q) => (q.state.data?.some((j) => j.state === "queued" || j.state === "running") ? 5000 : false),
  });
}

/** project 通道上的资产事件；返回是否已处理 */
export function applyAssetEvent(qc: QueryClient, pid: string, e: EventFrame): boolean {
  if (!isEvent(e, "asset.ready")) return false;
  void qc.invalidateQueries({ queryKey: ak.all(pid) });
  void qc.invalidateQueries({ queryKey: ak.jobs(pid) });
  for (const id of e.data.assetIds) void qc.invalidateQueries({ queryKey: ak.asset(id), exact: true });
  return true;
}

export function invalidateAssets(qc: QueryClient, pid: string) {
  void qc.invalidateQueries({ queryKey: ak.all(pid) });
  void qc.invalidateQueries({ queryKey: ak.jobs(pid) });
  void qc.invalidateQueries({ queryKey: ["asset"] });
}
